import React, { useState } from "react";
import { FaGripVertical, FaTrash } from "react-icons/fa";
import { DndContext, PointerSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from "@dnd-kit/sortable";
import { Checkbox, TextInput } from "../atoms";
import {
  useAddTaskChecklistItemMutation,
  useUpdateTaskChecklistItemMutation,
  useToggleTaskChecklistItemMutation,
  useRemoveTaskChecklistItemMutation,
  useReorderTaskChecklistMutation,
} from "../../store";
import useAuth from "../../Hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import { useDirtyDraft } from "./nestedOverlay";
import { countChecklist } from "./taskChecklistUtils";

// İş detayı "Kontrol listesi" bölümü. Contract 1.1: checklist/add|update|toggle|remove|reorder
// hepsi { task, … } → { checklist, checklistCount }. Yetki: can.edit; yoksa salt okunur.

const MAX_TEXT = 500;
const errText = (e, fallback) => e?.data?.message || e?.message || fallback;
const toTransform = (t) => (t ? `translate3d(${Math.round(t.x)}px, ${Math.round(t.y)}px, 0)` : undefined);

function ItemText({ item, canEdit, onSave }) {
  const [draft, setDraft] = useState(null);
  useDirtyDraft(draft !== null && draft.trim() !== item.text);

  const commit = async () => {
    if (draft === null) return;
    const v = draft.trim();
    if (!v || v === item.text) {
      setDraft(null);
      return;
    }
    const ok = await onSave(v);
    if (ok) setDraft(null);
  };

  if (draft !== null) {
    return (
      <TextInput
        autoFocus
        aria-label="Madde metni"
        value={draft}
        maxLength={MAX_TEXT}
        className="!py-1 text-sm"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation(); // çekmecenin Esc kapatmasına gitmesin
            setDraft(null);
          }
        }}
      />
    );
  }
  const textClass = `text-sm break-words ${item.done ? "text-gray-400 line-through" : "text-gray-800"}`;
  if (!canEdit) return <span className={textClass}>{item.text}</span>;
  return (
    <button
      type="button"
      aria-label={`Maddeyi düzenle: ${item.text}`}
      onClick={() => setDraft(item.text)}
      className={`text-left w-full rounded-md px-1 -mx-1 hover:bg-gray-100 ${textClass}`}
    >
      {item.text}
    </button>
  );
}

function ChecklistRow({ item, canEdit, onToggle, onSaveText, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item._id,
    disabled: !canEdit,
  });
  return (
    <li
      ref={setNodeRef}
      data-testid="checklist-item"
      style={{ transform: toTransform(transform), transition }}
      className={`group flex items-start gap-2 rounded-lg px-1 py-1 hover:bg-gray-50 ${isDragging ? "opacity-50" : ""}`}
    >
      {canEdit && (
        <button
          type="button"
          aria-label="Sırayı değiştir"
          className="mt-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <FaGripVertical className="w-3 h-3" aria-hidden="true" />
        </button>
      )}
      <Checkbox
        label=""
        checked={Boolean(item.done)}
        disabled={!canEdit}
        className="shrink-0 !gap-0"
        onChange={(e) => onToggle(item, e.target.checked)}
      />
      <div className="flex-1 min-w-0 pt-0.5">
        <ItemText item={item} canEdit={canEdit} onSave={(text) => onSaveText(item, text)} />
      </div>
      {canEdit && (
        <button
          type="button"
          aria-label={`Maddeyi sil: ${item.text}`}
          onClick={() => onRemove(item)}
          className="mt-1 text-gray-300 hover:text-fifth"
        >
          <FaTrash className="w-3 h-3" aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

function TaskChecklist({ task }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showError } = useAlert();
  const [addItem, { isLoading: adding }] = useAddTaskChecklistItemMutation();
  const [updateItem] = useUpdateTaskChecklistItemMutation();
  const [toggleItem] = useToggleTaskChecklistItemMutation();
  const [removeItem] = useRemoveTaskChecklistItemMutation();
  const [reorder] = useReorderTaskChecklistMutation();
  const [newText, setNewText] = useState("");
  useDirtyDraft(newText.trim() !== "");
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const canEdit = Boolean(task.can?.edit);
  const items = task.checklist || [];
  const { total, done } = task.checklistCount || countChecklist(items);
  const pct = total ? Math.round((done / total) * 100) : 0;

  const run = async (fn, args, fallback) => {
    try {
      await fn({ token, task: task._id, ...args }).unwrap();
      return true;
    } catch (e) {
      showError(errText(e, fallback));
      return false;
    }
  };

  const onAdd = async () => {
    const text = newText.trim();
    if (!text || adding) return;
    if (await run(addItem, { text }, "Madde eklenemedi.")) setNewText("");
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = items.map((i) => i._id);
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;
    run(reorder, { itemIds: arrayMove(ids, from, to) }, "Sıralama kaydedilemedi.");
  };

  if (!canEdit && !items.length) return null;

  return (
    <section className="space-y-2" data-testid="task-checklist">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-SemiBold text-gray-700">Kontrol listesi</h3>
        {total > 0 && (
          <span className="text-xs font-Medium text-gray-500" data-testid="checklist-progress">
            {done}/{total}
          </span>
        )}
      </div>
      {total > 0 && (
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className={`h-full transition-all ${done === total ? "bg-sixth" : "bg-primary"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((i) => i._id)} strategy={verticalListSortingStrategy}>
          <ul className="space-y-0.5">
            {items.map((item) => (
              <ChecklistRow
                key={item._id}
                item={item}
                canEdit={canEdit}
                onToggle={(it, next) =>
                  run(toggleItem, { itemId: it._id, done: next }, "Madde güncellenemedi.")
                }
                onSaveText={(it, text) => run(updateItem, { itemId: it._id, text }, "Madde kaydedilemedi.")}
                onRemove={(it) => run(removeItem, { itemId: it._id }, "Madde silinemedi.")}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      {canEdit && (
        <TextInput
          aria-label="Yeni madde"
          placeholder="Madde ekle ve Enter'a bas"
          value={newText}
          maxLength={MAX_TEXT}
          disabled={adding}
          className="!py-1.5 text-sm"
          onChange={(e) => setNewText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAdd();
            } else if (e.key === "Escape" && newText) {
              e.preventDefault();
              e.stopPropagation();
              setNewText("");
            }
          }}
        />
      )}
    </section>
  );
}

export default TaskChecklist;
